import type { WorkflowDefinition } from "@/stores/workflowsStore";
import type { AIWorkflowDefinition } from "@/types/aiChat";
import { resolveDescriptionTemplates } from "@/utils/resolveDescriptionTemplates";
import { tokenAmountToString } from "./tokenAmount";

/** Input type hint used to select the appropriate rich input widget for a template prop. */
export type TemplatePropInputType =
  | "address"
  | "contractAddress"
  | "recipientAddress"
  | "email"
  | "chainSelector"
  | "tokenSelector"
  | "multiTokenSelector"
  | "coinSelector"
  | "multiCoinSelector"
  | "tokenAmount"
  | "telegram-chat"
  | "pushover-recipient"
  | "pushbullet-device"
  | "webpush-subscription"
  | "slack-channel"
  | "storkAsset"
  | "textarea"
  | "password"
  | "polymarketUser"
  | "twitterUsername"
  | "rrule"
  | "notification-channel"
  | "morphoVault"
  | "csvTable"
  | "googleSheetTemplateUrl"
  | "googleSheetUrl"
  | "morphoMarket";

/**
 * A single field extracted from a workflow node payload.
 * Used to let users browse and select any field to convert to a template prop.
 */
export interface WorkflowField {
  /** Unique ID for this field */
  id: string;
  /** Node ID in the workflow definition */
  nodeId: string;
  /** Node type (e.g., "send-erc20") */
  nodeType: string;
  /** Human-readable node label */
  nodeLabel: string;
  /** Dot-notation path within the payload (e.g., "config.recipient") */
  fieldPath: string;
  /** Human-readable field name */
  fieldName: string;
  /** Current value of the field */
  value: string;
  /** Whether this field is already a variable reference */
  isVariable: boolean;
  /** Auto-detected category (if any) */
  category?: SuggestionCategory;
  /** Suggested input type based on field/value analysis */
  inputType?: TemplatePropInputType;
  /** Enum values from the action schema (for dropdown rendering). */
  schemaEnum?: (string | number)[];
  /** Human-readable labels for each enum value. */
  schemaEnumLabels?: string[];
  /** Schema format hint (e.g., "exchange" for exchange selector with logos). */
  schemaFormat?: string;
  /** Original schema type for the field (e.g., "number", "string", "integer") */
  schemaType?: string;
}

export type SuggestionCategory = "address" | "url" | "amount" | "identifier" | "text";

// Patterns for identifying parameterizable values
const ETH_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const URL_RE = /^https?:\/\/.+/;
const VARIABLE_REF_RE = /^\{\{[^}]+\}\}$/;
const CONDITION_FIELD_PATH_RE = /^condition\.\$(and|or)\[(\d+)]/;

/** Schema property shape with extension fields for token/chain metadata. */
type SchemaPropType = {
  format?: string;
  type?: string;
  enum?: (string | number)[];
  enumNames?: string[];
  enumLabels?: string[];
  "x-token-field"?: string;
  "x-chain-field"?: string;
  "x-token-address"?: string;
  "x-chain-id"?: number;
  "x-token-resolve"?: string;
};

/** Parses a condition that may be a JSON string or an object. */
export function parseCondition(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }
  if (value && typeof value === "object") return value as Record<string, unknown>;
  return undefined;
}

/** Extracts the node ID from a variable reference like "{{nodeId.result.field}}". */
function extractNodeIdFromVariable(variableKey: string): string | undefined {
  const cleaned = variableKey.replace(/^\{\{|\}\}$/g, "");
  return cleaned.split(".")[0] || undefined;
}

// Field names that are likely parameterizable
const INTERESTING_FIELD_NAMES = new Set([
  "address",
  "recipient",
  "sender",
  "to",
  "from",
  "wallet",
  "account",
  "url",
  "endpoint",
  "amount",
  "value",
  "threshold",
  "limit",
  "chainid",
  "chain_id",
  "tokenaddress",
  "token_address",
  "contractaddress",
  "contract_address",
  "username",
  "user_name",
  "email",
  "channel",
  "chatid",
  "chat_id",
  "groupid",
  "group_id",
]);

// Field names to skip (not useful as template props)
const SKIP_FIELD_NAMES = new Set([
  "type",
  "method",
  "format",
  "encoding",
  "content-type",
  "contenttype",
  "accept",
  "mode",
  "action",
  "operation",
]);

// Payload keys that are complex structures derived from other fields — not
// user-configurable on their own. eventAbi is fetched from the contract
// address (users select via EventAbiSelector), and filter is built via
// FilterBuilder. These are skipped entirely in template prop extraction.
const DERIVED_OBJECT_KEYS = new Set(["eventAbi", "filter"]);

// Condition comparison operators used in if/else node payloads.
// Only scalar-valued operators — $in/$nin use arrays and aren't extractable as single-value props.
const CONDITION_OPERATORS = new Set(["$gte", "$gt", "$lte", "$lt", "$eq", "$ne"]);

const OPERATOR_LABELS: Record<string, string> = {
  $gte: ">=",
  $gt: ">",
  $lte: "<=",
  $lt: "<",
  $eq: "=",
  $ne: "!=",
};

/** Maps action schema `format` hints to template prop `inputType` values. */
const FORMAT_TO_INPUT_TYPE: Record<string, WorkflowField["inputType"]> = {
  "telegram-chat": "telegram-chat",
  "pushover-recipient": "pushover-recipient",
  "pushbullet-device": "pushbullet-device",
  "webpush-subscription": "webpush-subscription",
  "slack-channel": "slack-channel",
  email: "email",
  "email-from": "email",
  "contract-address": "contractAddress",
  "recipient-address": "recipientAddress",
  "chain-id": "chainSelector",
  "token-address": "tokenSelector",
  "token-amount": "tokenAmount",
  "coin-id": "coinSelector",
  textarea: "textarea",
  password: "password",
  "polymarket-user": "polymarketUser",
  "morpho-vault": "morphoVault",
  "stork-asset": "storkAsset",
  "google-sheet-url": "googleSheetUrl",
};

/** Field-name-based inputType detection — mirrors sidebar (FormFieldBlockchain) fallbacks. */
const FIELD_NAME_TO_INPUT_TYPE: Record<string, WorkflowField["inputType"]> = {
  chainId: "chainSelector",
  chainid: "chainSelector",
  srcChainId: "chainSelector",
  dstChainId: "chainSelector",
  destChainId: "chainSelector",
  coinId: "coinSelector",
  coinid: "coinSelector",
  coinIds: "coinSelector",
  coinids: "coinSelector",
  contractAddress: "contractAddress",
  contractaddress: "contractAddress",
  rrule: "rrule",
};

/**
 * Flattens a schema property that may use `oneOf` into a single resolved object.
 * Returns the first `oneOf` alternative that has a `format` hint, falling back to the
 * first alternative, or the property itself if `oneOf` is not present.
 */
function resolveOneOf(schemaProp: Record<string, unknown>): Record<string, unknown> {
  const oneOf = schemaProp.oneOf as Record<string, unknown>[] | undefined;
  if (!Array.isArray(oneOf) || oneOf.length === 0) return schemaProp;
  // Prefer the alternative with a format hint (most specific)
  return oneOf.find(alt => alt.format) ?? oneOf[0];
}

/**
 * Resolves inputType for a field from action schema format, type, or field name.
 * Priority: format → schema property type → field name (mirrors FormFieldBlockchain).
 * Also looks inside `oneOf` alternatives for format/type hints.
 */
function resolveInputType(
  schemaProp: { format?: string; type?: string; oneOf?: unknown[] } | undefined,
  fieldName: string,
): WorkflowField["inputType"] | undefined {
  if (!schemaProp) return FIELD_NAME_TO_INPUT_TYPE[fieldName];
  const resolved = resolveOneOf(schemaProp as Record<string, unknown>);
  // 1. Check format hint
  if (resolved.format && FORMAT_TO_INPUT_TYPE[resolved.format as string]) {
    return FORMAT_TO_INPUT_TYPE[resolved.format as string];
  }
  // 2. Check custom schema type (e.g., type: "chain-id")
  if (resolved.type && FORMAT_TO_INPUT_TYPE[resolved.type as string]) {
    return FORMAT_TO_INPUT_TYPE[resolved.type as string];
  }
  // 3. Check field name
  return FIELD_NAME_TO_INPUT_TYPE[fieldName];
}

interface ValueMatch {
  category: SuggestionCategory;
  inputType?: WorkflowField["inputType"];
}

function categorizeValue(value: string, fieldPath: string): ValueMatch | null {
  const leaf = lastSegment(fieldPath).toLowerCase();

  // ETH address
  if (ETH_ADDRESS_RE.test(value)) {
    const isRecipient = ["recipient", "to", "receiver"].some(k => leaf.includes(k));
    return {
      category: "address",
      inputType: isRecipient ? "recipientAddress" : "address",
    };
  }

  // URL/endpoint
  if (URL_RE.test(value)) {
    return { category: "url" };
  }

  // Check field name for interesting patterns
  if (INTERESTING_FIELD_NAMES.has(leaf)) {
    return { category: "identifier" };
  }

  // Numeric-looking values in amount/value/threshold fields
  if (["amount", "value", "threshold", "limit", "price", "balance"].some(k => leaf.includes(k))) {
    return { category: "amount" };
  }

  // Address-like field names
  if (["address", "wallet", "account", "recipient", "sender"].some(k => leaf.includes(k))) {
    return { category: "address", inputType: "address" };
  }

  return null;
}

function walkPayload(
  obj: Record<string, unknown>,
  prefix: string,
  callback: (fieldPath: string, value: unknown) => void,
  skipConditions = false,
): void {
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "string" || typeof value === "number") {
      // Skip condition strings on if-nodes — they are JSON-serialised condition
      // objects that will be parsed and handled by extractConditionFields.
      if (skipConditions && key === "condition" && typeof value === "string") {
        continue;
      }
      callback(path, value);
    } else if (Array.isArray(value)) {
      if (DERIVED_OBJECT_KEYS.has(key)) continue;
      for (let i = 0; i < value.length; i++) {
        const elem = value[i];
        if (typeof elem === "string" || typeof elem === "number") {
          callback(`${path}[${i}]`, elem);
        } else if (elem !== null && typeof elem === "object") {
          walkPayload(elem as Record<string, unknown>, `${path}[${i}]`, callback, skipConditions);
        }
      }
    } else if (value !== null && typeof value === "object") {
      if (DERIVED_OBJECT_KEYS.has(key)) continue;
      // Skip condition objects with logical operators — handled by extractConditionFields.
      if (key === "condition" && hasLogicalOperator(value as Record<string, unknown>)) {
        continue;
      }
      walkPayload(value as Record<string, unknown>, path, callback, skipConditions);
    }
  }
}

/** Returns true if the object has $and or $or keys (i.e., is a condition structure). */
function hasLogicalOperator(obj: Record<string, unknown>): boolean {
  return "$and" in obj || "$or" in obj;
}

/**
 * Extracts comparison values from if/else condition structures as selectable fields.
 *
 * Condition payloads look like:
 *   { "condition": { "$and": [ { "{{var}}": { "$gte": "1" } } ] } }
 *
 * This function produces fields with paths like `condition.$and[0].$gte`
 * and human-readable names like "Formatted Balance (>=)".
 */
function extractConditionFields(
  nodeId: string,
  nodeLabel: string,
  nodeType: string,
  condition: Record<string, unknown>,
  nodes?: Record<string, { type: string }>,
  defMap?: Map<string, { payloadSchema?: { properties?: Record<string, unknown> } }>,
): WorkflowField[] {
  const fields: WorkflowField[] = [];

  for (const [logicalOp, rules] of Object.entries(condition)) {
    if (!Array.isArray(rules)) continue;

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule || typeof rule !== "object") continue;

      const entries = Object.entries(rule as Record<string, unknown>);
      if (entries.length !== 1) continue;

      const [variableKey, operators] = entries[0];
      if (!operators || typeof operators !== "object") continue;

      for (const [op, compValue] of Object.entries(operators as Record<string, unknown>)) {
        if (!CONDITION_OPERATORS.has(op)) continue;
        if (typeof compValue !== "string" && typeof compValue !== "number") continue;

        const strValue = String(compValue);
        // Skip values that are already variable references.
        if (VARIABLE_REF_RE.test(strValue) || strValue.includes("{{")) continue;

        const fieldPath = `condition.${logicalOp}[${i}].${op}`;

        // Derive a readable name from the variable ref and operator.
        const varName =
          variableKey
            .replace(/^\{\{|\}\}$/g, "")
            .split(".")
            .pop() || "value";
        const opLabel = OPERATOR_LABELS[op] || op;
        const fieldName = `${humanizeFieldPath(varName)} (${opLabel})`;

        const match = categorizeValue(strValue, varName);

        const numVal = Number(strValue);
        const inferredType = strValue.trim() !== "" && Number.isFinite(numVal) ? "number" : undefined;

        // Try to resolve inputType from the referenced node's action schema.
        let resolvedInputType = match?.inputType;
        if (!resolvedInputType && nodes && defMap) {
          const refNodeId = extractNodeIdFromVariable(variableKey);
          if (refNodeId && findTokenAmountSchema(refNodeId, nodes, defMap)) {
            resolvedInputType = "tokenAmount";
          }
        }

        fields.push({
          id: `${nodeId}::${fieldPath}`,
          nodeId,
          nodeType,
          nodeLabel,
          fieldPath,
          fieldName,
          value: strValue,
          isVariable: false,
          category: match?.category ?? "amount",
          inputType: resolvedInputType,
          schemaType: inferredType,
        });
      }
    }
  }

  return fields;
}

/**
 * Merges a field into an existing prop, upgrading the type if the new field
 * has a more specific schema type, and coercing the default value to match.
 * Returns true if the prop was modified.
 */
export function mergeFieldIntoProp(
  existingProp: { type: string; default?: string | number; properties?: { inputType?: string } },
  field: { schemaType?: string; inputType?: string },
): boolean {
  // Never upgrade tokenAmount props to number — they must stay as strings.
  const isTokenAmount = existingProp.properties?.inputType === "tokenAmount" || field.inputType === "tokenAmount";
  if (isTokenAmount) return false;

  if (existingProp.type === "string" && (field.schemaType === "number" || field.schemaType === "integer")) {
    existingProp.type = field.schemaType;
    if (typeof existingProp.default === "string") {
      const trimmed = existingProp.default.trim();
      if (trimmed !== "") {
        const n = Number(trimmed);
        if (Number.isFinite(n)) existingProp.default = n;
      }
    }
    return true;
  }
  return false;
}

function lastSegment(fieldPath: string): string {
  const parts = fieldPath.replace(/\[\d+\]/g, "").split(".");
  return parts[parts.length - 1] || fieldPath;
}

/**
 * Extracts ALL string fields from every node in a workflow definition.
 * Returns a flat list of WorkflowField objects organized by node,
 * letting users browse and select any field to convert to a template prop.
 *
 * Duplicate names get auto-suffixed (e.g., "Log 1", "Log 2").
 */
export function extractAllFields(
  definition: WorkflowDefinition | AIWorkflowDefinition,
  defMap?: Map<string, { payloadSchema?: { properties?: Record<string, unknown> } }>,
): WorkflowField[] {
  if (!definition?.nodes) return [];

  const fields: WorkflowField[] = [];

  // Pre-compute node labels with dedup counters for duplicate names.
  // Matches the canvas naming: titleOverride first, then humanized type.
  // (node.description is NOT used for titles — it's the AI-generated subtitle.)
  const nodeEntries = Object.entries(definition.nodes).filter(([, n]) => n.payload);
  const nodeLabels = new Map<string, string>();
  for (const [nodeId, node] of nodeEntries) {
    // Access titleOverride directly — it exists on WorkflowNode but not AIWorkflowNode,
    // so use a structural cast rather than any to safely read it from the runtime object.
    const titleOverride = (node as { titleOverride?: string }).titleOverride;
    const resolvedTitle = titleOverride
      ? resolveDescriptionTemplates(titleOverride, node.payload as Record<string, unknown>)
      : undefined;

    // Use the AI-generated title if available, then definition name, then humanized type
    const definitionName = (node as { name?: string }).name;
    nodeLabels.set(nodeId, resolvedTitle || definitionName || humanizeType(node.type));
  }

  for (const [nodeId, node] of nodeEntries) {
    const nodeLabel = nodeLabels.get(nodeId) || humanizeType(node.type);

    const payload = node.payload as Record<string, unknown>;
    const extractedPaths = new Set<string>();
    const isIfNode = node.type === "if";

    walkPayload(
      payload,
      "",
      (fieldPath, value) => {
        const strValue = typeof value === "number" ? String(value) : (value as string);
        const leaf = lastSegment(fieldPath).toLowerCase();
        if (SKIP_FIELD_NAMES.has(leaf)) return;

        extractedPaths.add(fieldPath);
        const isVariable = VARIABLE_REF_RE.test(strValue) || strValue.includes("{{");
        const match = !isVariable ? categorizeValue(strValue, fieldPath) : null;

        fields.push({
          id: `${nodeId}::${fieldPath}`,
          nodeId,
          nodeType: node.type,
          nodeLabel,
          fieldPath,
          fieldName: humanizeFieldPath(fieldPath),
          value: strValue,
          isVariable,
          category: match?.category,
          inputType: match?.inputType,
        });
      },
      isIfNode,
    );

    // For if/else nodes, extract comparison values from condition structures.
    // The condition may be stored as a JSON string (FilterBuilder serialises via
    // JSON.stringify) or as an object (AI/backend). Handle both forms.
    if (isIfNode) {
      const condition = parseCondition(payload.condition);
      if (condition && hasLogicalOperator(condition)) {
        const conditionFields = extractConditionFields(
          nodeId,
          nodeLabel,
          node.type,
          condition,
          definition.nodes as Record<string, { type: string }>,
          defMap,
        );
        for (const cf of conditionFields) {
          extractedPaths.add(cf.fieldPath);
          fields.push(cf);
        }
      }
    }

    // Also surface schema properties with defaults that aren't in the payload
    if (defMap) {
      const actionDef = defMap.get(node.type);
      const schemaProps = actionDef?.payloadSchema?.properties as
        | Record<string, { type?: string; default?: unknown; description?: string }>
        | undefined;
      if (schemaProps) {
        for (const [key, prop] of Object.entries(schemaProps)) {
          if (extractedPaths.has(key)) continue;
          if (prop.default === undefined || prop.default === null) continue;
          const leaf = key.toLowerCase();
          if (SKIP_FIELD_NAMES.has(leaf)) continue;

          const defaultVal = prop.default;
          if (typeof defaultVal !== "string" && typeof defaultVal !== "number") continue;
          const strValue = String(defaultVal);

          const match = categorizeValue(strValue, key);

          fields.push({
            id: `${nodeId}::${key}`,
            nodeId,
            nodeType: node.type,
            nodeLabel,
            fieldPath: key,
            fieldName: humanizeFieldPath(key),
            value: strValue,
            isVariable: false,
            category: match?.category,
            inputType: match?.inputType,
          });
        }
      }
    }
  }

  // Enrich inputType, schemaType, and enum from action schema (format, type, field name)
  if (defMap) {
    for (const field of fields) {
      const actionDef = defMap.get(field.nodeType);
      const props = actionDef?.payloadSchema?.properties as Record<string, SchemaPropType> | undefined;
      if (!props) continue;
      const rootField = field.fieldPath.split(/[.\[]/)[0];
      const schemaProp = props[rootField];
      const resolvedProp = schemaProp
        ? (resolveOneOf(schemaProp as Record<string, unknown>) as SchemaPropType)
        : undefined;
      // Preserve the original schema type (number, integer, etc.)
      const schemaType = resolvedProp?.type ?? schemaProp?.type;
      if (schemaType && !field.schemaType) {
        field.schemaType = schemaType;
      }
      // Schema-based inputType (from format/type hints) takes priority over
      // name-based heuristics set by categorizeValue (e.g., "vaultAddress" → "address").
      const schemaInputType = resolveInputType(schemaProp, rootField);
      if (schemaInputType) {
        field.inputType = schemaInputType;
      }
      // Extract enum values + labels from schema for dropdown rendering
      if (resolvedProp?.enum && !field.schemaEnum) {
        field.schemaEnum = resolvedProp.enum;
        const rawLabels = resolvedProp.enumNames ?? resolvedProp.enumLabels;
        if (rawLabels) field.schemaEnumLabels = rawLabels;
      }
      if (schemaProp?.format && !field.schemaFormat) {
        field.schemaFormat = schemaProp.format;
      }
    }
  }

  // Hide chainSelector fields from nodes that also have a morphoVault field,
  // since the MorphoVaultSelector has a built-in chain filter. Showing both
  // would let users create a redundant standalone chain prop.
  const nodesWithMorphoVault = new Set(fields.filter(f => f.inputType === "morphoVault").map(f => f.nodeId));
  if (nodesWithMorphoVault.size > 0) {
    return fields.filter(f => !(f.inputType === "chainSelector" && nodesWithMorphoVault.has(f.nodeId)));
  }

  return fields;
}

/**
 * Finds the first schema property with token-amount format for a given node,
 * resolving through oneOf alternatives.
 */
function findTokenAmountSchema(
  nodeId: string,
  nodes: Record<string, { type: string; payload?: Record<string, unknown> }>,
  defMap: Map<string, { payloadSchema?: { properties?: Record<string, unknown> } }>,
): { schemaProp: SchemaPropType; node: { type: string; payload?: Record<string, unknown> } } | null {
  const node = nodes[nodeId];
  if (!node) return null;
  const refDef = defMap.get(node.type);
  const refProps = refDef?.payloadSchema?.properties as Record<string, Record<string, unknown>> | undefined;
  if (!refProps) return null;
  for (const prop of Object.values(refProps)) {
    const resolved = resolveOneOf(prop);
    if (resolved.format === "token-amount") {
      return { schemaProp: resolved as SchemaPropType, node };
    }
  }
  return null;
}

/**
 * For condition-sourced tokenAmount fields, resolves the referenced node's schema
 * to find chain/token metadata. Extracts the variable key from the condition rule
 * to determine which node to look up.
 */
function resolveConditionRefSchema(
  ifPayload: Record<string, unknown>,
  logicalOp: string,
  ruleIndex: number,
  nodes: Record<string, { type: string; payload?: Record<string, unknown> }>,
  defMap: Map<string, { payloadSchema?: { properties?: Record<string, unknown> } }>,
): { schemaProp: SchemaPropType; node: { type: string; payload?: Record<string, unknown> } } | null {
  const condition = parseCondition(ifPayload.condition);
  if (!condition) return null;
  const rules = condition[`$${logicalOp}`] as unknown[] | undefined;
  if (!rules || ruleIndex >= rules.length) return null;
  const rule = rules[ruleIndex] as Record<string, unknown> | undefined;
  if (!rule) return null;
  const variableKey = Object.keys(rule)[0];
  if (!variableKey) return null;
  const refNodeId = extractNodeIdFromVariable(variableKey);
  if (!refNodeId) return null;
  return findTokenAmountSchema(refNodeId, nodes, defMap);
}

/**
 * Enriches existing template props with `inputType` from action schema hints.
 * Iterates all sources per prop, resolving through oneOf and condition references.
 */
export function enrichPropsWithInputType(
  props: Array<{
    type?: string;
    default?: string | number;
    properties?: {
      inputType?: string;
      enum?: (string | number)[];
      enumLabels?: string[];
      sources?: Array<{ nodeId: string; fieldPath: string }>;
      chainId?: number;
      chainPropKey?: string;
      tokenAddress?: string;
      tokenAddressPropKey?: string;
      tokenResolve?: "erc4626" | "erc4626-vault";
    };
  }>,
  definition: WorkflowDefinition | AIWorkflowDefinition,
  defMap: Map<string, { payloadSchema?: { properties?: Record<string, unknown> } }>,
): void {
  if (!definition?.nodes) return;

  const nodes = definition.nodes as Record<string, { type: string; payload?: Record<string, unknown> }>;

  for (const prop of props) {
    const sources = prop.properties?.sources;

    // Try all sources to find schema hints (inputType, chain/token metadata).
    // A prop may have multiple sources (e.g., condition field + payload field),
    // and only some sources will have useful schema info.
    let resolvedSchemaProp: SchemaPropType | undefined;
    let resolvedNode: (typeof nodes)[string] | undefined;

    for (const source of sources ?? []) {
      const node = nodes[source.nodeId];
      if (!node) continue;

      const actionDef = defMap.get(node.type);
      const schemaProps = actionDef?.payloadSchema?.properties as Record<string, SchemaPropType> | undefined;

      const rootField = source.fieldPath.split(/[.\[]/)[0];
      const srcSchemaProp = schemaProps?.[rootField];
      const srcResolved = srcSchemaProp
        ? (resolveOneOf(srcSchemaProp as Record<string, unknown>) as SchemaPropType)
        : undefined;

      // Enrich inputType from schema — schema hints (format/type) take priority
      // over name-based heuristics (e.g., "vaultAddress" → "address" should be
      // overridden by format: "morpho-vault" → "morphoVault").
      if (srcSchemaProp) {
        const resolved = resolveInputType(srcSchemaProp, rootField);
        if (resolved) {
          if (!prop.properties) prop.properties = {};
          prop.properties!.inputType = resolved;
          resolvedSchemaProp = srcResolved;
          resolvedNode = node;
        }
      }

      // For condition fields on built-in nodes (e.g. "if"), resolve inputType from the referenced node
      if (!prop.properties?.inputType) {
        const condMatch = source.fieldPath.match(CONDITION_FIELD_PATH_RE);
        if (condMatch && node.payload) {
          const refInfo = resolveConditionRefSchema(
            node.payload,
            condMatch[1],
            parseInt(condMatch[2], 10),
            nodes,
            defMap,
          );
          if (refInfo?.schemaProp.format) {
            if (!prop.properties) prop.properties = {};
            const mapped = FORMAT_TO_INPUT_TYPE[refInfo.schemaProp.format];
            if (mapped) {
              prop.properties!.inputType = mapped;
              resolvedSchemaProp = refInfo.schemaProp;
              resolvedNode = refInfo.node;
            }
          }
        }
      }

      // When inputType was already set (e.g., from field extraction), we still need
      // the resolved schema prop and node for downstream metadata (chain/token/enum).
      const isMetadataFallback = !!(prop.properties?.inputType && !resolvedSchemaProp && srcResolved);
      if (isMetadataFallback) {
        resolvedSchemaProp = srcResolved;
        resolvedNode = node;
      }

      // Upgrade prop type from "string" to "number" if schema says so,
      // but skip for tokenAmount props (their values are strings in smallest units).
      if (
        !isMetadataFallback &&
        srcSchemaProp?.type &&
        (srcSchemaProp.type === "number" || srcSchemaProp.type === "integer") &&
        prop.properties?.inputType !== "tokenAmount"
      ) {
        if (prop.type === "string") {
          prop.type = srcSchemaProp.type;
        }
      }

      // Enrich enum values and labels from schema (for dropdown rendering).
      // Always refresh from schema so stored templates pick up renames/labels.
      if (srcResolved?.enum) {
        if (!prop.properties) prop.properties = {};
        const rawLabels = srcResolved.enumNames ?? srcResolved.enumLabels;
        prop.properties.enum = srcResolved.enum;
        if (rawLabels) prop.properties.enumLabels = rawLabels;
      }

      // Once we have inputType and schema metadata, no need to check more sources
      if (prop.properties?.inputType && resolvedSchemaProp) break;
    }

    // Downgrade tokenAmount props to "string" — they must stay as strings
    // in smallest units. Covers legacy templates stored as type "number".
    // Hoisted out of the source loop so it also runs for sourceless props.
    if (prop.properties?.inputType === "tokenAmount" && prop.type !== "string") {
      prop.type = "string";
      if (typeof prop.default === "number") {
        prop.default = tokenAmountToString(prop.default);
      }
    }

    // Upgrade coinSelector → multiCoinSelector when the prop is an array-typed
    // field (e.g. `coinIds` on `coingecko-get-token-price`). Field-name heuristics
    // default to single, but array props need the multi variant.
    if (prop.properties?.inputType === "coinSelector" && prop.type === "array") {
      prop.properties.inputType = "multiCoinSelector";
    }

    // For tokenAmount and morphoVault props, resolve chainId and tokenAddress from schema annotations
    if (
      (prop.properties?.inputType === "tokenAmount" || prop.properties?.inputType === "morphoVault") &&
      resolvedNode?.payload
    ) {
      const sp = resolvedSchemaProp;

      const tokenFieldName = sp?.["x-token-field"] || "tokenAddress";
      const chainFieldName = sp?.["x-chain-field"] || "chainId";

      // Resolve tokenAddress — hardcoded in schema, from payload literal, or variable reference
      if (prop.properties.tokenAddress === undefined && !prop.properties.tokenAddressPropKey) {
        const hardcodedToken = sp?.["x-token-address"];
        if (hardcodedToken) {
          prop.properties.tokenAddress = hardcodedToken;
        } else {
          const effectivePayload = resolvedNode.payload as Record<string, unknown> | undefined;
          const tokenVal = effectivePayload?.[tokenFieldName];
          if (typeof tokenVal === "string") {
            const propKey = extractPropKeyFromVariable(tokenVal);
            if (propKey) {
              prop.properties.tokenAddressPropKey = propKey;
            } else if (ETH_ADDRESS_RE.test(tokenVal)) {
              prop.properties.tokenAddress = tokenVal;
            }
          }
        }
      }

      // Propagate tokenResolve from schema annotation (e.g. "erc4626" for Morpho vaults)
      // so TemplatePropValueInput can pass it to TokenAmountInput for on-chain resolution.
      const rawTokenResolve = sp?.["x-token-resolve"];
      if ((rawTokenResolve === "erc4626" || rawTokenResolve === "erc4626-vault") && !prop.properties.tokenResolve) {
        prop.properties.tokenResolve = rawTokenResolve;
      }

      // Resolve chainId — hardcoded in schema, from payload literal, or variable reference
      if (prop.properties.chainId === undefined && !prop.properties.chainPropKey) {
        const hardcodedChain = sp?.["x-chain-id"];
        if (hardcodedChain !== undefined) {
          prop.properties.chainId = hardcodedChain;
        } else {
          const effectivePayload = resolvedNode.payload as Record<string, unknown> | undefined;
          const chainVal = effectivePayload?.[chainFieldName];
          if (typeof chainVal === "number") {
            prop.properties.chainId = chainVal;
          } else if (typeof chainVal === "string") {
            const propKey = extractPropKeyFromVariable(chainVal);
            if (propKey) {
              prop.properties.chainPropKey = propKey;
            } else {
              const trimmed = chainVal.trim();
              const parsed = trimmed !== "" ? Number(trimmed) : NaN;
              if (!isNaN(parsed)) prop.properties.chainId = parsed;
            }
          }
        }
      }
    }
  }
}

/**
 * Auto-infer `groupKey` on template props that come from the same source node
 * and cover the shape the ConnectorInputModal knows how to render as a single
 * combined widget — chain+token+amount (`TokenChainAmountInput`) or
 * chain+token (`TokenChainInput`). Runs AFTER `enrichPropsWithInputType` so
 * the `chainPropKey` / `tokenAddressPropKey` pointers are already set on any
 * `tokenAmount` props (they're the anchors for pass 1 below).
 *
 * Without this, a cross-chain swap template would surface 5 disconnected
 * fields (src chain, token in, amount in, dst chain, token out) and let the
 * user enter impossible combinations like "ETH on Base" + amount-unit "BNB"
 * + chain "BSC". With this, the modal collapses them into one "buy_eth"
 * (or similar) sell widget + a destination chain+token widget.
 *
 * Two passes, so a single node with BOTH a tokenAmount-anchored group and
 * a leftover chain+token pair (typical swap) gets two distinct groups:
 *
 *   Pass 1: each `tokenAmount` prop that has resolved `chainPropKey` +
 *     `tokenAddressPropKey` pointers → stamp `groupKey: <nodeId>` on all
 *     three (amount + chain target + token target).
 *   Pass 2: remaining chain+token props from the same source node that form
 *     an unambiguous 1-chain + 1-token pair → stamp a distinct `groupKey`.
 *
 * Also wires `chainPropKey` on the tokenSelector sibling in each group so
 * the token dropdown filters by the group's chain. Respects any manually-
 * set `groupKey` — existing values are never overwritten.
 */
export function autoInferGroupKeys(
  props: Array<{
    key: string;
    properties?: {
      inputType?: string;
      groupKey?: string;
      chainPropKey?: string;
      tokenAddressPropKey?: string;
      sources?: Array<{ nodeId: string; fieldPath: string }>;
    };
  }>,
): void {
  const byKey = new Map<string, (typeof props)[number]>();
  for (const p of props) byKey.set(p.key, p);

  // Suffix helper: if multiple groups want the same base name (e.g. two
  // swap nodes both producing a "sell" group), give later ones deterministic
  // `_2`, `_3` suffixes so they don't collide.
  const usedGroupKeys = new Set<string>();
  for (const p of props) {
    if (p.properties?.groupKey) usedGroupKeys.add(p.properties.groupKey);
  }
  const makeGroupKey = (base: string): string => {
    if (!usedGroupKeys.has(base)) {
      usedGroupKeys.add(base);
      return base;
    }
    for (let n = 2; ; n++) {
      const candidate = `${base}_${n}`;
      if (!usedGroupKeys.has(candidate)) {
        usedGroupKeys.add(candidate);
        return candidate;
      }
    }
  };

  // Pass 1: tokenAmount-anchored groups (3 members)
  for (const amountProp of props) {
    if (amountProp.properties?.groupKey) continue;
    if (amountProp.properties?.inputType !== "tokenAmount") continue;

    const chainKey = amountProp.properties?.chainPropKey;
    const tokenKey = amountProp.properties?.tokenAddressPropKey;
    if (!chainKey || !tokenKey) continue;

    const chainProp = byKey.get(chainKey);
    const tokenProp = byKey.get(tokenKey);
    if (!chainProp || !tokenProp) continue;
    if (chainProp.properties?.inputType !== "chainSelector") continue;
    if (tokenProp.properties?.inputType !== "tokenSelector") continue;
    if (chainProp.properties?.groupKey || tokenProp.properties?.groupKey) continue;

    const nodeId = amountProp.properties?.sources?.[0]?.nodeId;
    if (!nodeId) continue;

    const members = [amountProp, chainProp, tokenProp];
    const gk = makeGroupKey(inferSemanticGroupName(members) ?? nodeId);
    for (const p of members) {
      if (!p.properties) p.properties = {};
      p.properties.groupKey = gk;
    }
    if (!tokenProp.properties!.chainPropKey) {
      tokenProp.properties!.chainPropKey = chainProp.key;
    }
  }

  // Pass 2: leftover chain+token pairs (2 members), same nodeId, 1+1 only
  const leftoverByNodeId = new Map<string, typeof props>();
  for (const prop of props) {
    if (prop.properties?.groupKey) continue;
    const inputType = prop.properties?.inputType;
    if (inputType !== "chainSelector" && inputType !== "tokenSelector") continue;
    const nodeId = prop.properties?.sources?.[0]?.nodeId;
    if (!nodeId) continue;
    if (!leftoverByNodeId.has(nodeId)) leftoverByNodeId.set(nodeId, []);
    leftoverByNodeId.get(nodeId)!.push(prop);
  }
  for (const [nodeId, bucket] of leftoverByNodeId) {
    const chains = bucket.filter(p => p.properties?.inputType === "chainSelector");
    const tokens = bucket.filter(p => p.properties?.inputType === "tokenSelector");
    // Strict 1+1 — anything ambiguous (e.g. a node with two chains and two
    // tokens already got its src/in trio claimed in pass 1; the leftover
    // dst/out pair still has exactly one of each, which is the green path).
    if (chains.length !== 1 || tokens.length !== 1) continue;

    const [chainProp] = chains;
    const [tokenProp] = tokens;
    const members = [chainProp, tokenProp];
    const gk = makeGroupKey(inferSemanticGroupName(members) ?? nodeId);
    chainProp.properties!.groupKey = gk;
    tokenProp.properties!.groupKey = gk;
    if (!tokenProp.properties!.chainPropKey) {
      tokenProp.properties!.chainPropKey = chainProp.key;
    }
  }
}

/**
 * Infer a human-readable group key from the fieldPaths in the group. Swap
 * nodes follow strong naming conventions (`srcChainId`/`tokenIn`/`amountIn`
 * on the source side; `dstChainId`/`tokenOut` on the destination side), so
 * we can promote the group label from the raw node id ("buy_eth") to
 * something the end-user understands ("Sell" / "Buy").
 *
 * Returns null when the fieldPaths don't unambiguously match — callers
 * should fall back to the node id so non-swap actions (e.g. a morpho
 * deposit with chainId + tokenAddress + amount) still group correctly, just
 * with their node-id label.
 */
function inferSemanticGroupName(
  members: Array<{ properties?: { sources?: Array<{ fieldPath: string }> } }>,
): string | null {
  const joined = members.flatMap(m => m.properties?.sources?.map(s => s.fieldPath.toLowerCase()) ?? []).join("|");

  // Destination side — dstChainId, tokenOut, destinationChainId, amountOut, …
  const isDestination =
    /tokenout|dstchain|destinationchain|amountout|tokento|chainto|\bdestination\b|\bto\b|\bout\b/.test(joined);
  // Source side — srcChainId, tokenIn, sourceChainId, amountIn, fixedAmount, …
  const isSource =
    /tokenin|srcchain|sourcechain|amountin|fixedamount|tokenfrom|chainfrom|\bsource\b|\bfrom\b|\bin\b/.test(joined);

  if (isSource && !isDestination) return "sell";
  if (isDestination && !isSource) return "buy";
  return null;
}

/** Extracts the prop key from a `{{$props.keyName}}` variable reference, or returns undefined. */
function extractPropKeyFromVariable(value: string): string | undefined {
  const match = value.match(/^\{\{\s*\$props\.([\w-]+)\s*\}\}$/);
  return match?.[1];
}

function humanizeType(type: string): string {
  return type
    .split(/[-_]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function humanizeFieldPath(fieldPath: string): string {
  const leaf = lastSegment(fieldPath);
  return leaf
    .split(/[-_]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
